import { ThemeConfig } from '@nylas/core';
import { Component, h, Prop, State, Event, EventEmitter, Listen, Element, Watch } from '@stencil/core';

/**
 * The `checkbox-component` component is a UI component that allows users to select a checkbox.
 * This component is used in the scheduling form to input checkbox type inputs.
 */
@Component({
  tag: 'checkbox-component',
  styleUrl: 'checkbox-component.scss',
  shadow: true,
})
export class CheckboxComponent {
  @Element() el!: HTMLElement;
  private readonly componentType: string = 'checkbox';

  /**
   * The name of the checkbox. This is used to identify the checkbox when submitting a form.
   */
  @Prop() name: string = 'checkbox';
  /**
   * The default value of the checkbox. This is the value that is displayed when the checkbox is rendered.
   */
  @Prop() checked?: boolean;
  /**
   * The label of the checkbox. This is displayed next to the checkbox.
   */
  @Prop() label: string = '';
  /**
   * The property to make the checkbox read-only. If true, the checkbox cannot be edited.
   */
  @Prop() readOnly: boolean = false;
  /**
   * Whether the checkbox is required. If true, the checkbox must be checked when submitting a form.
   * Default is false. If the checkbox is required and not checked, an error message is displayed.
   */
  @Prop() required: boolean = false;
  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;
  /**
   * Error message to display when the checkbox is required and not checked.
   */
  @Prop() errorMessage: string = '';
  /**
   * The checked state of the checkbox.
   */
  @State() isChecked!: boolean;
  /**
   * The error message state.
   */
  @State() error: string = '';

  /**
   * This event is fired when the checkbox is toggled.
   */
  @Event() nylasFormCheckboxToggled!: EventEmitter<{
    checked: boolean;
    name: string;
    label: string;
  }>;

  @Watch('checked')
  checkedPropChangedHandler(newValue: boolean) {
    if (typeof newValue === 'boolean') {
      this.isChecked = newValue;
    }
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.el.style.setProperty(`${key}`, value);
      }
    }
  }

  // Lifecycle events
  connectedCallback() {
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.el.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    this.el.setAttribute('component-type', this.componentType);
  }

  componentDidLoad() {
    this.isChecked = this.checked || false;
  }

  // Event listeners
  /**
   * Listen for the bookingFormSubmitted event to validate the input value when the form is submitted.
   */
  @Listen('bookingFormSubmitted', { target: 'document' })
  handleBookingFormSubmitted(event: CustomEvent) {
    if (this.required && !this.isChecked) {
      this.error = this.errorMessage || `${this.label} is required.`;
    }
    if (this.error) {
      event.preventDefault();
    }
  }

  // Methods
  toggleCheck() {
    this.error = '';
    this.isChecked = !this.isChecked;
    this.nylasFormCheckboxToggled.emit({
      checked: this.isChecked,
      name: this.name,
      label: this.label,
    });
  }

  render() {
    return (
      <div class="checkbox-container">
        <label title={this.readOnly ? 'read-only field' : undefined} class={{ readonly: this.readOnly }}>
          <input
            type="checkbox"
            disabled={this.readOnly}
            readOnly={this.readOnly}
            name={this.name}
            title={this.readOnly ? 'read-only field' : undefined}
            checked={this.isChecked}
            onChange={() => this.toggleCheck()}
          />
          <span class="checkmark"></span>
          <slot name="label">
            <span class="label" part="checkbox-label-span">
              {this.label}
              {this.required && <span class="required">*</span>}
            </span>
          </slot>
        </label>
        {this.error && <p class="error">{this.error}</p>}
      </div>
    );
  }
}
